import React from 'react';
import {Alert, Stack, Tooltip, Typography} from '@mui/material';
import DistrictChildRelationIcon from './DistrictChildRelationIcon';

interface ReadOnlyMessageBannerProps {
  ownerCompanyName: string;
  visibilityOrigin?: string;
}

/**
 * Banner displayed at the top of shared message detail / preview views.
 * Highlights the district → child relationship so shared content origin
 * is unmistakable. Uses the same themed SvgIcon as the inline list badge
 * so partners can pattern-match the relationship across list and detail.
 */
export const ReadOnlyMessageBanner: React.FC<ReadOnlyMessageBannerProps> = ({
  ownerCompanyName,
  visibilityOrigin,
}) => {
  const districtLabel = ownerCompanyName?.trim() || 'the district';
  const tooltipLabel = `Shared by ${districtLabel} (District) · Read-only on your partner account`;

  return (
    <Alert severity='info' sx={{mb: 2}} icon={false}>
      <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap'>
        <Tooltip title={tooltipLabel} arrow>
          <DistrictChildRelationIcon
            role='img'
            aria-label={tooltipLabel}
            sx={{
              fontSize: 28,
              color: 'primary.main',
              cursor: 'help',
              stroke: 'currentColor',
              strokeWidth: 4,
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Typography variant='body2'>
          Shared from <strong>{districtLabel}</strong>
          {visibilityOrigin === 'district_parent'
            ? ' · Managed by the district parent.'
            : ''}
        </Typography>
      </Stack>
    </Alert>
  );
};
